import { useState, useEffect } from "react";
import { apiClient } from "../../apiClient";
import { Pie } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";

ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels);

interface TotaleContatti {
  positivi: number;
  negativi: number;
  in_attesa: number;
  disponibili_futuro: number;
}

// 🚀 AGGIORNATO: Esteso il tipo accettato per includere nativamente "startup"
interface TotaleContattiPieProps {
  tipo: "linkedin" | "small" | "startup" | "general";
}

export default function TotaleContattiPie({ tipo }: TotaleContattiPieProps) {
  const [totale, setTotale] = useState<TotaleContatti | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchTotale = async () => {
    try {
      setLoading(true);
      const res = await apiClient.get(`contatti/performance/?tipo=${tipo}`);

      const dati = res.data.performance;
      let tot: TotaleContatti = {
        positivi: 0,
        negativi: 0,
        in_attesa: 0,
        disponibili_futuro: 0,
      };

      if (Array.isArray(dati)) {
        tot = {
          positivi: dati.reduce((sum: number, a: any) => sum + (a.positivi || 0), 0),
          negativi: dati.reduce((sum: number, a: any) => sum + (a.negativi || 0), 0),
          in_attesa: dati.reduce((sum: number, a: any) => sum + (a.in_attesa || 0), 0),
          disponibili_futuro: dati.reduce((sum: number, a: any) => sum + (a.disponibilita || 0), 0),
        };
      }

      setTotale(tot);
    } catch (err) {
      console.error("Errore fetch totale contatti:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTotale();
  }, [tipo]); // Si riattiva in automatico quando la tendina dell'Overview passa a "startup" o "general"

  if (loading) return <p className="text-secondary/50">Caricamento...</p>;
  if (!totale) return <p className="text-red-500">Nessun dato disponibile</p>;

  const values = [
    totale.positivi,
    totale.negativi,
    totale.in_attesa,
    totale.disponibili_futuro,
  ];

  const hasData = values.some(v => v > 0);

  const data = {
    labels: ["Positivi", "Negativi", "In attesa", "Disponibili per progetti futuri"],
    datasets: [
      {
        data: hasData ? values : [1, 1, 1, 1],
        backgroundColor: ["#22c55e", "#ef4444", "#facc15", "#3b82f6"],
        borderColor: ["#16a34a", "#dc2626", "#ca8a04", "#2563eb"],
        borderWidth: 2,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      tooltip: { enabled: true },
      legend: { display: false },
      datalabels: {
        color: "#fff",
        font: { weight: "bold" as const, size: 14 },
        formatter: (value: number, context: any) => {
          const dataset = context.chart.data.datasets[0].data;
          const total = dataset.reduce((sum: number, val: number) => sum + val, 0);
          if (total === 0 || !hasData) return "";
          const percent = (value / total) * 100;
          return percent >= 5
            ? percent.toLocaleString("it-IT", { maximumFractionDigits: 1 }) + "%"
            : "";
        },
      },
    },
  };

  return (
    <div className="flex flex-col items-center p-6">
      <div className="relative w-50 h-50 sm:w-75 sm:h-75">
        <Pie data={data} options={options} />
      </div>
      <div className="flex gap-6 mt-6 flex-wrap justify-center">
        {data.labels.map((label, idx) => (
          <div key={idx} className="flex items-center gap-2 text-sm text-secondary">
            <span
              className="w-4 h-4 rounded-full"
              style={{ backgroundColor: data.datasets[0].backgroundColor[idx] }}
            ></span>
            {label}
          </div>
        ))}
      </div>
    </div>
  );
}